import { Vector3 } from '../engine/types/Vector3'
import { Quaternion } from '../engine/types/Quaternion'
import { AlignmentMode, UpVectorMode } from '../engine/field/FieldTypes'

export class CurveUtils {
  // ==================== LINE CURVES ====================

  /**
   * Get point on line between start and end
   */
  public static getPointOnLine(start: Vector3, end: Vector3, t: number): Vector3 {
    return new Vector3(
      start.x + (end.x - start.x) * t,
      start.y + (end.y - start.y) * t,
      start.z + (end.z - start.z) * t
    )
  }

  /**
   * Get direction vector from start to end
   */
  public static getDirection(start: Vector3, end: Vector3): Vector3 {
    return new Vector3(
      end.x - start.x,
      end.y - start.y,
      end.z - start.z
    ).normalize()
  }

  /**
   * Get tangent of line (constant direction)
   */
  static getLineTangent(start: Vector3, end: Vector3): Vector3 {
    return end.clone().subtract(start).normalize()
  }

  // ==================== CIRCLE CURVES ====================

  /**
   * Get point on circle at angle (in radians)
   */
  static getPointOnCircle(
    center: Vector3,
    radius: number,
    angle: number,
    normal: Vector3 = new Vector3(0, 1, 0)
  ): Vector3 {
    // Create circle in XZ plane, then rotate to desired orientation
    const x = Math.cos(angle) * radius
    const z = Math.sin(angle) * radius
    const point = new Vector3(x, 0, z)
    
    // If normal is not Y-axis, rotate the circle
    if (!normal.equals(new Vector3(0, 1, 0))) {
      const up = new Vector3(0, 1, 0)
      const quaternion = Quaternion.lookDir(normal, up)
      point.applyQuaternion(quaternion)
    }
    
    return point.add(center)
  }

  /**
   * Get tangent on circle at angle
   */
  static getCircleTangent(
    angle: number,
    normal: Vector3 = new Vector3(0, 1, 0)
  ): Vector3 {
    // Tangent is perpendicular to radius
    const tangent = new Vector3(-Math.sin(angle), 0, Math.cos(angle))
    
    // Rotate if normal is not Y-axis
    if (!normal.equals(new Vector3(0, 1, 0))) {
      const up = new Vector3(0, 1, 0)
      const quaternion = Quaternion.lookDir(normal, up)
      tangent.applyQuaternion(quaternion)
    }
    
    return tangent.normalize()
  }

  /**
   * Get center of circle
   */
  public static getCircleCenter(radius: number, normal: Vector3): Vector3 {
    // For now, just return origin
    return new Vector3(0, 0, 0)
  }

  // ==================== SPIRAL CURVES ====================

  /**
   * Get point on spiral at parameter t (0-1)
   */
  static getPointOnSpiral(
    center: Vector3,
    radius: number,
    height: number,
    turns: number,
    t: number
  ): Vector3 {
    const angle = t * turns * Math.PI * 2
    const y = t * height
    const x = Math.cos(angle) * radius
    const z = Math.sin(angle) * radius
    
    return new Vector3(x, y, z).add(center)
  }

  /**
   * Get point on spiral with radius tapering
   */
  static getPointOnTaperedSpiral(
    center: Vector3,
    startRadius: number,
    endRadius: number,
    height: number,
    turns: number,
    t: number
  ): Vector3 {
    const angle = t * turns * Math.PI * 2
    const y = t * height
    const currentRadius = startRadius + (endRadius - startRadius) * t
    const x = Math.cos(angle) * currentRadius
    const z = Math.sin(angle) * currentRadius
    
    return new Vector3(x, y, z).add(center)
  }

  /**
   * Get tangent on spiral at parameter t
   */
  static getSpiralTangent(
    radius: number,
    height: number,
    turns: number,
    t: number
  ): Vector3 {
    const angle = t * turns * Math.PI * 2
    const angleStep = turns * Math.PI * 2
    
    // Derivative of spiral parametric equations
    const dx = -Math.sin(angle) * radius * angleStep
    const dy = height
    const dz = Math.cos(angle) * radius * angleStep
    
    return new Vector3(dx, dy, dz).normalize()
  }

  // ==================== GRID CURVES ====================

  /**
   * Get point on grid at row/col coordinates
   */
  static getPointOnGrid(
    center: Vector3,
    spacing: number,
    row: number,
    col: number,
    gridSize: number
  ): Vector3 {
    const offsetX = (col - (gridSize - 1) / 2) * spacing
    const offsetZ = (row - (gridSize - 1) / 2) * spacing
    
    return new Vector3(offsetX, 0, offsetZ).add(center)
  }

  // ==================== HEXAGONAL CURVES ====================

  /**
   * Get point on hexagonal grid at ring/position coordinates
   */
  static getPointOnHex(
    center: Vector3,
    spacing: number,
    ring: number,
    positionInRing: number
  ): Vector3 {
    if (ring === 0) {
      return center.clone()
    }
    
    const pointsInRing = ring * 6
    const angle = (positionInRing / pointsInRing) * Math.PI * 2
    const distance = ring * spacing
    
    const x = Math.cos(angle) * distance
    const z = Math.sin(angle) * distance
    
    return new Vector3(x, 0, z).add(center)
  }

  /**
   * Get point on hexagonal pattern distributed on sphere surface
   */
  static getPointOnHexSphere(
    center: Vector3,
    radius: number,
    ring: number,
    positionInRing: number
  ): Vector3 {
    if (ring === 0) {
      return new Vector3(0, radius, 0).add(center) // North pole
    }
    
    // Calculate latitude and longitude for hex pattern on sphere
    const maxRing = 10 // Approximate max ring for sphere tessellation
    const lat = Math.PI * (0.5 - ring / maxRing) // From north pole to south pole
    const pointsInRing = ring * 6
    const lon = (positionInRing / pointsInRing) * Math.PI * 2
    
    // Convert spherical coordinates to Cartesian
    const x = radius * Math.cos(lat) * Math.cos(lon)
    const y = radius * Math.sin(lat)
    const z = radius * Math.cos(lat) * Math.sin(lon)
    
    return new Vector3(x, y, z).add(center)
  }

  /**
   * Get normal vector at point on sphere (radial direction)
   */
  static getSphereNormal(
    center: Vector3,
    spherePoint: Vector3
  ): Vector3 {
    return spherePoint.clone().subtract(center).normalize()
  }

  /**
   * Get tangent vector on sphere surface at given point
   */
  static getSphereTangent(
    center: Vector3,
    spherePoint: Vector3,
    ring: number,
    positionInRing: number
  ): Vector3 {
    if (ring === 0) {
      return new Vector3(1, 0, 0) // Arbitrary tangent at north pole
    }
    
    // Calculate tangent direction along the ring
    const pointsInRing = ring * 6
    const angle = (positionInRing / pointsInRing) * Math.PI * 2
    const tangent = new Vector3(-Math.sin(angle), 0, Math.cos(angle))
    
    // Project tangent onto sphere surface
    const normal = this.getSphereNormal(center, spherePoint)
    const projectedTangent = tangent.clone().subtract(
      normal.clone().multiplyScalar(tangent.dot(normal))
    )
    
    return projectedTangent.normalize()
  }

  /**
   * Get point on grid pattern distributed on sphere surface
   */
  static getPointOnGridSphere(
    center: Vector3,
    radius: number,
    row: number,
    col: number,
    gridSize: number
  ): Vector3 {
    // Convert grid coordinates to spherical coordinates
    // Latitude: from north pole to south pole
    const lat = Math.PI * (row / (gridSize - 1)) - Math.PI / 2
    
    // Longitude: full rotation around sphere
    const lon = 2 * Math.PI * (col / gridSize)
    
    // Convert spherical coordinates to Cartesian
    const x = radius * Math.cos(lat) * Math.cos(lon)
    const y = radius * Math.sin(lat)
    const z = radius * Math.cos(lat) * Math.sin(lon)
    
    return new Vector3(x, y, z).add(center)
  }

  /**
   * Get tangent vector on sphere surface for grid pattern
   */
  static getGridSphereTangent(
    center: Vector3,
    spherePoint: Vector3,
    row: number,
    col: number,
    gridSize: number
  ): Vector3 {
    // Calculate tangent direction along longitude (constant latitude)
    const lat = Math.PI * (row / (gridSize - 1)) - Math.PI / 2
    const lon = 2 * Math.PI * (col / gridSize)
    
    // Tangent along longitude direction
    const tangent = new Vector3(-Math.sin(lon), 0, Math.cos(lon))
    
    // Project tangent onto sphere surface
    const normal = this.getSphereNormal(center, spherePoint)
    const projectedTangent = tangent.clone().subtract(
      normal.clone().multiplyScalar(tangent.dot(normal))
    )
    
    return projectedTangent.normalize()
  }

  /**
   * Get binormal vector on sphere surface for grid pattern
   */
  static getGridSphereBinormal(
    center: Vector3,
    spherePoint: Vector3,
    row: number,
    col: number,
    gridSize: number
  ): Vector3 {
    // Calculate binormal direction along latitude (constant longitude)
    const lat = Math.PI * (row / (gridSize - 1)) - Math.PI / 2
    const lon = 2 * Math.PI * (col / gridSize)
    
    // Binormal along latitude direction
    const binormal = new Vector3(
      -Math.cos(lon) * Math.sin(lat),
      Math.cos(lat),
      -Math.sin(lon) * Math.sin(lat)
    )
    
    return binormal.normalize()
  }

  // ==================== CURVE UTILITIES ====================

  /**
   * Calculate arc length of parametric curve using Simpson's rule
   */
  static calculateArcLength(
    curveFunction: (t: number) => Vector3,
    startT: number = 0,
    endT: number = 1,
    segments: number = 100
  ): number {
    const deltaT = (endT - startT) / segments
    let length = 0
    
    for (let i = 0; i < segments; i++) {
      const t1 = startT + i * deltaT
      const t2 = startT + (i + 1) * deltaT
      const p1 = curveFunction(t1)
      const p2 = curveFunction(t2)
      length += p1.distance(p2)
    }
    
    return length
  }

  /**
   * Find parameter t for given arc length
   */
  static findParameterForArcLength(
    curveFunction: (t: number) => Vector3,
    targetLength: number,
    totalLength?: number,
    precision: number = 0.001
  ): number {
    if (!totalLength) {
      totalLength = this.calculateArcLength(curveFunction)
    }
    
    if (targetLength <= 0) return 0
    if (targetLength >= totalLength) return 1
    
    // Binary search for the parameter
    let minT = 0
    let maxT = 1
    let currentT = targetLength / totalLength // Initial guess
    
    while (maxT - minT > precision) {
      const currentLength = this.calculateArcLength(curveFunction, 0, currentT)
      
      if (currentLength < targetLength) {
        minT = currentT
      } else {
        maxT = currentT
      }
      
      currentT = (minT + maxT) / 2
    }
    
    return currentT
  }

  // ==================== IMPROVED FRENET-FRAME CALCULATIONS ====================

  /**
   * Get improved tangent vector with better numerical stability
   */
  static getImprovedTangent(
    curveFunction: (t: number) => Vector3,
    t: number,
    dt: number = 0.001
  ): Vector3 {
    const t1 = Math.max(0, t - dt);
    const t2 = Math.min(1, t + dt);
    const p1 = curveFunction(t1);
    const p2 = curveFunction(t2);
    
    const tangent = new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z);
    return tangent.normalize();
  }

  /**
   * Get improved normal vector (curvature direction)
   */
  static getImprovedNormal(
    tangentFunction: (t: number) => Vector3,
    t: number,
    dt: number = 0.001
  ): Vector3 {
    const t1 = Math.max(0, t - dt);
    const t2 = Math.min(1, t + dt);
    const tan1 = tangentFunction(t1);
    const tan2 = tangentFunction(t2);
    
    const normal = new Vector3(tan2.x - tan1.x, tan2.y - tan1.y, tan2.z - tan1.z);
    
    // Handle degenerate case (straight lines)
    if (normal.length() < 0.001) {
      const tangent = tangentFunction(t);
      const worldUp = new Vector3(0, 1, 0);
      let lineNormal = new Vector3().crossVectors(tangent, worldUp);
      
      if (lineNormal.length() < 0.001) {
        lineNormal = new Vector3(1, 0, 0);
      }
      return lineNormal.normalize();
    }
    return normal.normalize();
  }

  /**
   * Get improved binormal vector
   */
  static getImprovedBinormal(tangent: Vector3, normal: Vector3): Vector3 {
    return tangent.clone().cross(normal).normalize();
  }

  // ==================== 3D ORIENTATION CALCULATION ====================

  /**
   * Calculate proper 3D orientation based on alignment mode and up vector mode
   * This implements the mathematical principles from the other AI
   */
  static calculateOrientation(
    tangent: Vector3,
    normal: Vector3,
    binormal: Vector3,
    orientation: 'aligned' | 'perpendicular',
    upMode: 'world' | 'path'
  ): { right: Vector3; up: Vector3; forward: Vector3 } {
    
    // SAFETY: Ensure all input vectors are normalized
    const normalizedTangent = tangent.clone().normalize();
    const normalizedNormal = normal.clone().normalize();
    const normalizedBinormal = binormal.clone().normalize();
    
    // Step 1: Determine desired forward direction
    let desiredForward: Vector3;
    if (orientation === 'aligned') {
      desiredForward = normalizedNormal.clone();  // Aligned to curve
    } else {
      desiredForward = normalizedTangent.clone(); // Perpendicular to curve
    }

    // Step 2: Determine up vector
    let desiredUp: Vector3;
    if (upMode === 'world') {
      desiredUp = new Vector3(0, 1, 0);
    } else {
      desiredUp = normalizedBinormal.clone().negate(); // Path-following
    }

    // Step 3: CRITICAL - Two different algorithms!
    let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;

    if (upMode === 'world') {
      // WORLD UP: Up-vector is FIXED on (0,1,0)
      finalUp = new Vector3(0, 1, 0); // NEVER change!
      
      // Right calculate
      finalRight = new Vector3().crossVectors(finalUp, desiredForward);
      
      // Parallelity check
      if (finalRight.length() < 0.001) {
        finalRight = new Vector3(1, 0, 0); // Fallback
      }
      finalRight.normalize();
      
      // Forward NEW calculate for orthogonality
      finalForward = new Vector3().crossVectors(finalRight, finalUp);
      finalForward.normalize();
      
    } else {
      // PATH UP: Normal Frenet-Frame calculation
      finalForward = desiredForward.clone().normalize();
      finalUp = desiredUp.clone().normalize();
      
      // Right calculate
      finalRight = new Vector3().crossVectors(finalUp, finalForward);
      finalRight.normalize();
      
      // Up correct for orthogonality
      finalUp = new Vector3().crossVectors(finalForward, finalRight);
      finalUp.normalize();
    }

    // SAFETY CHECK: Verify orthogonality and unit length
    const rightLength = finalRight.length();
    const upLength = finalUp.length();
    const forwardLength = finalForward.length();
    
    if (Math.abs(rightLength - 1.0) > 0.001 || 
        Math.abs(upLength - 1.0) > 0.001 || 
        Math.abs(forwardLength - 1.0) > 0.001) {
      console.warn('⚠️ calculateOrientation: Non-unit vectors detected!', {
        orientation, upMode,
        rightLength, upLength, forwardLength
      });
      // Force normalization
      finalRight.normalize();
      finalUp.normalize();
      finalForward.normalize();
    }
    
    // SAFETY CHECK: Verify orthogonality
    const rightUpDot = finalRight.dot(finalUp);
    const rightForwardDot = finalRight.dot(finalForward);
    const upForwardDot = finalUp.dot(finalForward);
    
    if (Math.abs(rightUpDot) > 0.01 || Math.abs(rightForwardDot) > 0.01 || Math.abs(upForwardDot) > 0.01) {
      console.warn('⚠️ calculateOrientation: Non-orthogonal vectors detected!', {
        orientation, upMode,
        rightUpDot, rightForwardDot, upForwardDot
      });
    }

    return { right: finalRight, up: finalUp, forward: finalForward };
  }

  // ==================== SPECIFIC CURVE IMPLEMENTATIONS ====================

  /**
   * Get complete Frenet frame for line
   */
  static getLineFrenetFrame(start: Vector3, end: Vector3, t: number): { tangent: Vector3; normal: Vector3; binormal: Vector3 } {
    const tangent = this.getLineTangent(start, end);
    
    // For lines, normal is perpendicular to tangent
    const worldUp = new Vector3(0, 1, 0);
    let normal = new Vector3().crossVectors(tangent, worldUp);
    
    if (normal.length() < 0.001) {
      normal = new Vector3(1, 0, 0);
    }
    normal.normalize();
    
    const binormal = new Vector3().crossVectors(tangent, normal).normalize();
    
    return { tangent, normal, binormal };
  }

  /**
   * Get complete Frenet frame for circle
   */
  static getCircleFrenetFrame(
    center: Vector3,
    radius: number,
    angle: number,
    normal: Vector3 = new Vector3(0, 1, 0)
  ): { tangent: Vector3; normal: Vector3; binormal: Vector3 } {
    const tangent = this.getCircleTangent(angle, normal);
    
    // For circles, normal points to center
    const position = this.getPointOnCircle(center, radius, angle, normal);
    const radialNormal = center.clone().subtract(position).normalize();
    
    const binormal = normal.clone().normalize();
    
    return { tangent, normal: radialNormal, binormal };
  }

  /**
   * Get complete Frenet frame for spiral
   */
  static getSpiralFrenetFrame(
    center: Vector3,
    radius: number,
    height: number,
    turns: number,
    t: number
  ): { tangent: Vector3; normal: Vector3; binormal: Vector3 } {
    const tangent = this.getSpiralTangent(radius, height, turns, t);
    
    // Get current position for normal calculation
    const position = this.getPointOnSpiral(center, radius, height, turns, t);
    const centerAtHeight = new Vector3(center.x, position.y, center.z);
    const radialDirection = centerAtHeight.subtract(position).normalize();
    
    const binormal = new Vector3().crossVectors(tangent, radialDirection).normalize();
    
    return { tangent, normal: radialDirection, binormal };
  }

  // ==================== 2D SURFACE ORIENTATION CALCULATIONS ====================

  /**
   * Calculate 2D surface orientation using the improved mathematical principles
   * This implements the surface orientation algorithms from the other AI
   */
  static calculate2DSurfaceOrientation(
    surfaceType: 'plane' | 'sphere',
    position: Vector3,
    planeNormal: Vector3 | null,
    sphereCenter: Vector3 | null,
    uParameter: number, // U coordinate on surface (0-1)
    vParameter: number, // V coordinate on surface (0-1)
    orientation: AlignmentMode,
    upMode: UpVectorMode
  ): { right: Vector3; up: Vector3; forward: Vector3 } {

    let tangentU: Vector3, tangentV: Vector3, binormal: Vector3;

    if (surfaceType === 'sphere') {
      // SPHERE CALCULATIONS
      const center = sphereCenter || new Vector3();
      const radius = position.distanceTo(center);
      
      // Convert to spherical coordinates
      const phi = uParameter * Math.PI * 2; // Longitude (0 to 2π)
      const theta = vParameter * Math.PI;   // Latitude (0 to π)
      
      // Tangent vectors (partial derivatives)
      tangentU = this.getSphereUTangent(phi, theta, radius);
      tangentV = this.getSphereVTangent(phi, theta, radius);
      
      // Surface normal (binormal) - points outward from sphere center
      binormal = position.clone().subtract(center).normalize();
      
    } else {
      // PLANE CALCULATIONS
      const normal = planeNormal || new Vector3(0, 1, 0);
      
      // For tilted plane (45° example), calculate tangent vectors
      tangentU = this.getPlaneUTangent(normal);
      tangentV = this.getPlaneVTangent(normal);
      
      // Surface normal (binormal)
      binormal = normal.clone().normalize();
    }

    // STEP 1: Determine desired Forward direction
    let desiredForward: Vector3;
    if (orientation === AlignmentMode.Aligned) {
      // Aligned: Forward points along surface normal (away from surface)
      desiredForward = binormal.clone();
    } else {
      // Perpendicular: Forward is tangential to surface (U-direction)
      desiredForward = tangentU.clone();
    }

    // STEP 2: Determine Up vector
    let desiredUp: Vector3;
    if (upMode === UpVectorMode.WorldUp) {
      desiredUp = new Vector3(0, 1, 0); // Always vertical
    } else {
      // Path: follows surface curvature (V-direction, negated for correct orientation)
      desiredUp = tangentV.clone().negate();
    }

    // STEP 3: Build orthogonal coordinate system using the improved algorithm
    let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;

    if (upMode === UpVectorMode.WorldUp) {
      // WORLD UP: Up vector is FIXED on (0,1,0)
      finalUp = new Vector3(0, 1, 0); // NEVER change!

      // Calculate Right vector
      finalRight = new Vector3().crossVectors(finalUp, desiredForward);

      // Parallelity check
      if (finalRight.length() < 0.001) {
        // Fallback when forward is parallel to world up
        finalRight = new Vector3(1, 0, 0); // Use world right as fallback
      }
      finalRight.normalize();

      // Recalculate Forward to ensure orthogonality
      finalForward = new Vector3().crossVectors(finalRight, finalUp);
      finalForward.normalize();

    } else {
      // PATH UP: Forward and Up follow surface, calculate Right
      finalForward = desiredForward.clone().normalize();
      finalUp = desiredUp.clone().normalize();

      // Calculate Right vector
      finalRight = new Vector3().crossVectors(finalUp, finalForward);
      finalRight.normalize();

      // Ensure orthogonality - recalculate Up
      finalUp = new Vector3().crossVectors(finalForward, finalRight);
      finalUp.normalize();
    }

    // STEP 4: Special case for tilted plane + aligned + path-up
    if (surfaceType === 'plane' && orientation === AlignmentMode.Aligned && upMode === UpVectorMode.PathUp) {
      // Apply the special rotations described by the other AI
      
      // Create rotation matrix from our vectors
      const matrix = new this.Matrix4().makeBasis(finalRight, finalUp, finalForward);
      
      // -90° rotation around yellow arrow (plane normal)
      const yellowArrowAxis = binormal.clone();
      const rotationMinus90 = this.quaternionFromAxisAngle(yellowArrowAxis, -Math.PI / 2);
      
      // Apply to current matrix
      let currentQuat = this.quaternionFromMatrix(matrix);
      let finalQuat = rotationMinus90.multiply(currentQuat);
      
      // +90° rotation around cyan vector (object forward)
      const cyanAxis = finalForward.clone();
      const rotationPlus90 = this.quaternionFromAxisAngle(cyanAxis, Math.PI / 2);
      finalQuat = rotationPlus90.multiply(finalQuat);
      
      // Extract final vectors from quaternion
      const finalMatrix = this.matrixFromQuaternion(finalQuat);
      finalRight = new Vector3(finalMatrix.elements[0], finalMatrix.elements[4], finalMatrix.elements[8]);
      finalUp = new Vector3(finalMatrix.elements[1], finalMatrix.elements[5], finalMatrix.elements[9]);
      finalForward = new Vector3(finalMatrix.elements[2], finalMatrix.elements[6], finalMatrix.elements[10]);
    }

    return {
      right: finalRight,
      up: finalUp,
      forward: finalForward
    };
  }

  /**
   * Get U-tangent vector for sphere (longitude direction)
   */
  private static getSphereUTangent(phi: number, theta: number, radius: number): Vector3 {
    // ∂P/∂u (longitude direction)
    return new Vector3(
      -radius * Math.sin(theta) * Math.sin(phi),
      0,
      radius * Math.sin(theta) * Math.cos(phi)
    ).normalize();
  }

  /**
   * Get V-tangent vector for sphere (latitude direction)
   */
  private static getSphereVTangent(phi: number, theta: number, radius: number): Vector3 {
    // ∂P/∂v (latitude direction)
    return new Vector3(
      radius * Math.cos(theta) * Math.cos(phi),
      -radius * Math.sin(theta),
      radius * Math.cos(theta) * Math.sin(phi)
    ).normalize();
  }

  /**
   * Get U-tangent vector for plane
   */
  private static getPlaneUTangent(normal: Vector3): Vector3 {
    // For a general plane, create a tangent perpendicular to normal
    // Use cross product with world axes to get consistent tangent
    let tangent: Vector3;
    if (Math.abs(normal.x) < 0.9) {
      tangent = new Vector3().crossVectors(new Vector3(1, 0, 0), normal);
    } else {
      tangent = new Vector3().crossVectors(new Vector3(0, 1, 0), normal);
    }
    return tangent.normalize();
  }

  /**
   * Get V-tangent vector for plane
   */
  private static getPlaneVTangent(normal: Vector3): Vector3 {
    const uTangent = this.getPlaneUTangent(normal);
    // V-tangent is perpendicular to both normal and U-tangent
    return new Vector3().crossVectors(normal, uTangent).normalize();
  }

  // ==================== UTILITY METHODS FOR SURFACE ORIENTATION ====================

  /**
   * Helper: Create Matrix4 from basis vectors (simplified)
   */
  private static Matrix4 = class {
    elements: number[] = new Array(16);
    
    makeBasis(right: Vector3, up: Vector3, forward: Vector3): this {
      this.elements[0] = right.x;   this.elements[4] = up.x;   this.elements[8] = forward.x;   this.elements[12] = 0;
      this.elements[1] = right.y;   this.elements[5] = up.y;   this.elements[9] = forward.y;   this.elements[13] = 0;
      this.elements[2] = right.z;   this.elements[6] = up.z;   this.elements[10] = forward.z;  this.elements[14] = 0;
      this.elements[3] = 0;         this.elements[7] = 0;      this.elements[11] = 0;         this.elements[15] = 1;
      return this;
    }
  };

  /**
   * Helper: Create quaternion from axis-angle
   */
  private static quaternionFromAxisAngle(axis: Vector3, angle: number): any {
    const halfAngle = angle / 2;
    const s = Math.sin(halfAngle);
    return {
      x: axis.x * s,
      y: axis.y * s,
      z: axis.z * s,
      w: Math.cos(halfAngle),
      multiply: function(q: any) {
        return {
          x: this.w * q.x + this.x * q.w + this.y * q.z - this.z * q.y,
          y: this.w * q.y + this.y * q.w + this.z * q.x - this.x * q.z,
          z: this.w * q.z + this.z * q.w + this.x * q.y - this.y * q.x,
          w: this.w * q.w - this.x * q.x - this.y * q.y - this.z * q.z,
          multiply: this.multiply
        };
      }
    };
  }

  /**
   * Helper: Extract quaternion from matrix (simplified)
   */
  private static quaternionFromMatrix(matrix: any): any {
    const m = matrix.elements;
    const trace = m[0] + m[5] + m[10];
    
    let w, x, y, z;
    if (trace > 0) {
      const s = Math.sqrt(trace + 1) * 2;
      w = 0.25 * s;
      x = (m[6] - m[9]) / s;
      y = (m[8] - m[2]) / s;
      z = (m[1] - m[4]) / s;
    } else {
      // Use largest diagonal element
      if (m[0] > m[5] && m[0] > m[10]) {
        const s = Math.sqrt(1 + m[0] - m[5] - m[10]) * 2;
        w = (m[6] - m[9]) / s;
        x = 0.25 * s;
        y = (m[1] + m[4]) / s;
        z = (m[2] + m[8]) / s;
      } else if (m[5] > m[10]) {
        const s = Math.sqrt(1 + m[5] - m[0] - m[10]) * 2;
        w = (m[8] - m[2]) / s;
        x = (m[1] + m[4]) / s;
        y = 0.25 * s;
        z = (m[6] + m[9]) / s;
      } else {
        const s = Math.sqrt(1 + m[10] - m[0] - m[5]) * 2;
        w = (m[1] - m[4]) / s;
        x = (m[2] + m[8]) / s;
        y = (m[6] + m[9]) / s;
        z = 0.25 * s;
      }
    }
    
    return {
      x, y, z, w,
      multiply: CurveUtils.quaternionFromAxisAngle.prototype.multiply
    };
  }

  /**
   * Helper: Create matrix from quaternion (simplified)
   */
  private static matrixFromQuaternion(q: any): any {
    const matrix = new this.Matrix4();
    const { x, y, z, w } = q;
    
    matrix.elements[0] = 1 - 2 * (y * y + z * z);
    matrix.elements[1] = 2 * (x * y + z * w);
    matrix.elements[2] = 2 * (x * z - y * w);
    
    matrix.elements[4] = 2 * (x * y - z * w);
    matrix.elements[5] = 1 - 2 * (x * x + z * z);
    matrix.elements[6] = 2 * (y * z + x * w);
    
    matrix.elements[8] = 2 * (x * z + y * w);
    matrix.elements[9] = 2 * (y * z - x * w);
    matrix.elements[10] = 1 - 2 * (x * x + y * y);
    
    return matrix;
  }
} 